import { t } from '@glory/i18n';
import { AlertTriangle, Info } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../lib/cn.ts';
import { Popover, PopoverContent, PopoverTrigger } from './popover.tsx';

export interface InfoHintContent {
  /** Заголовок поповера; по умолчанию — «Как это работает». */
  readonly title?: ReactNode;
  /** Объяснение: что это и как считается. */
  readonly text: ReactNode;
  /** Пример с реальными числами — после него вопросов обычно не остаётся. */
  readonly example?: ReactNode;
}

/**
 * Значок «i» с объяснением рядом с неочевидной функцией, полем или расчётом; `tone="warning"` — треугольник
 * предупреждения с объяснением, что не так и что сделать.
 * Открывается по нажатию, а не наведению: работает на телефоне и с клавиатуры.
 */
export function InfoHint({
  hint,
  label,
  tone = 'info',
  className,
}: {
  hint: InfoHintContent;
  /** Подпись кнопки для экранных дикторов: «Как это работает: Наценка». */
  label?: string;
  tone?: 'info' | 'warning';
  className?: string;
}) {
  const Icon = tone === 'warning' ? AlertTriangle : Info;
  return (
    <Popover>
      <PopoverTrigger
        className={cn(
          'inline-flex size-4 shrink-0 items-center justify-center rounded-full',
          'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
          tone === 'warning'
            ? 'text-danger hover:opacity-80'
            : 'text-muted hover:text-text focus-visible:text-text data-[state=open]:text-text',
          className,
        )}
        aria-label={label ?? t('fields.hint')}
      >
        <Icon className="size-3.5" aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" side="top" collisionPadding={12}>
        <div className="flex flex-col gap-1.5 text-small">
          <div className="font-bold text-text">{hint.title ?? t('fields.hint')}</div>
          <div className="text-text">{hint.text}</div>
          {hint.example === undefined ? null : (
            <div className="mt-0.5 rounded-sm bg-surface-2 px-2 py-1.5">
              <div className="text-caption font-bold text-muted">{t('fields.example')}</div>
              <div className="tabular-nums text-text">{hint.example}</div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
